
import { SidebarDrawer } from '@/components/custom/DrawerRight'
import { renderNodesAsParagraphs } from '@/features/screenplay/components/editor/plugins/SpAIWriterChatPlugin'
import { getCurrentNode } from '@/features/screenplay/components/editor/utils/NodeUtils'
import { useStoryStore } from '@/features/shared/stores/story.store'
import { useGenerateStoryMutation } from '@/features/story/queries/story.ai.queries'
import AI_Prompt from '@/shared/components/ui/ai-prompt'
import { Button } from '@/shared/components/ui/button'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import TypewriterTitle from '@/shared/components/ui/typing-text'
import { cn } from '@/shared/lib/utils'
import { AppError } from '@/shared/types/AppError'
import { SerializedContent } from '@storyverse/shared'
import { AnimatePresence, motion } from 'framer-motion'
import { $createTextNode } from 'lexical'
import { Check, ChevronDown, Globe } from 'lucide-react'
import { nanoid } from 'nanoid'
import { useState } from 'react'
import { AIChatMode, aIChatModeMetaData, useStAIWriterChat } from '../../StAIWriterChatProvider'
import { ParaNode } from '../nodes/ParaNode'

const StAIWriterChatPlugin = () => {
    const { isDrawerOpen, setIsDrawerOpen, inputNodes, title, mode, outputNodes, setOutputNodes } =
        useStAIWriterChat()
    const { editor } = useStoryStore()
    const [selectedLanguage, setSelectedLanguage] = useState('English')
    const LANGUAGES = ['English', 'Malayalam', 'Tamil']

    const generateStoryMutation = useGenerateStoryMutation({
        onSuccess: (data: SerializedContent[]) => {
            setOutputNodes(data)
        },
        onError: (error: AppError) => {
            console.error('Failed to generate AI output:', error)
        }
    })

    const isLoading = generateStoryMutation.isPending

    const handlePromptSubmit = async (text: string) => {
        generateStoryMutation.mutate({
            storyData: inputNodes,
            userInstruction: text,
            language: selectedLanguage,
        })
    }
    if (!editor) return null
    return (
        <SidebarDrawer
            isOpen={isDrawerOpen}
            onClose={() => setIsDrawerOpen(false)}
            title={aIChatModeMetaData[mode].title}
        >
            <div className="flex flex-col h-full p-4">
                <div className="flex-1 overflow-auto space-y-4 mb-4">
                    {inputNodes && renderNodesAsParagraphs(inputNodes)}
                    {outputNodes.length > 0 && (
                        <div className="border-t pt-4">
                            <p className="text-sm font-semibold text-muted-foreground mb-2">
                                AI Suggestion
                            </p>
                            {renderNodesAsParagraphs(outputNodes)}
                        </div>
                    )}
                </div>
                <AI_Prompt
                    onSubmit={handlePromptSubmit}
                    customButton={buildAIActionButton()}
                    placeholder={
                        isLoading
                            ? 'Wait... the scene is being built'
                            : aIChatModeMetaData[mode].promptPlaceholder
                    }
                />
            </div>
        </SidebarDrawer>
    )

    function buildAIActionButton() {
        if (isLoading) {
            return (
                <div className="flex-1 h-full">
                    <TypewriterTitle
                        sequences={[
                            {
                                text: 'Your story is on the assembly line...',
                                deleteAfter: true,
                            },
                            {
                                text: 'Loading your next masterpiece...',
                                deleteAfter: true,
                            },
                            { text: 'This story’s about to live.', deleteAfter: true },
                        ]}
                    />
                </div>
            )
        }

        if (outputNodes.length < 1) return buildLanguageDropdown()
        if (mode === AIChatMode.create_story && outputNodes.length > 0) return buildInsertButton()
        if (mode === AIChatMode.edit_story && outputNodes.length > 0) return buildReplaceButton()
    }

    function buildLanguageDropdown() {
        return (
            <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                    <Button
                        variant="ghost"
                        className="flex items-center gap-1 h-8 pl-1 pr-2 text-xs rounded-md dark:text-white hover:bg-black/10 dark:hover:bg-white/10 focus-visible:ring-1 focus-visible:ring-offset-0 focus-visible:ring-blue-500"
                    >
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={selectedLanguage}
                                initial={{ opacity: 0, y: -5 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: 5 }}
                                transition={{ duration: 0.15 }}
                                className="flex items-center gap-1"
                            >
                                <Globe className="w-4 h-4 opacity-50" />
                                {selectedLanguage}
                                <ChevronDown className="w-3 h-3 opacity-50" />
                            </motion.div>
                        </AnimatePresence>
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                    className={cn(
                        'min-w-[10rem]',
                        'border-black/10 dark:border-white/10',
                        'bg-gradient-to-b from-white via-white to-neutral-100 dark:from-neutral-950 dark:via-neutral-900 dark:to-neutral-800'
                    )}
                >
                    {LANGUAGES.map((lang) => (
                        <DropdownMenuItem
                            key={lang}
                            onSelect={() => setSelectedLanguage(lang)}
                            className="flex items-center justify-between gap-2"
                        >
                            <div className="flex items-center gap-2">
                                <Globe className="w-4 h-4 opacity-50" />
                                <span>{lang}</span>
                            </div>
                            {selectedLanguage === lang && (
                                <Check className="w-4 h-4 text-blue-500" />
                            )}
                        </DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>
        )
    }

    function buildInsertButton() {
        return (
            <Button
                className="flex-1"
                onClick={() => {
                    editor?.update(() => {
                        const currentNode = getCurrentNode() as ParaNode
                        if (!currentNode) return
                        const newNode = outputNodes.map((node) => {
                            const textContent = node.children
                                .map((child: any) => child.text)
                                .join(' ')
                            const para = ParaNode.create(nanoid())
                            para.append($createTextNode(textContent))
                            return para
                        })
                        const [firstNode, ...restNodes] = newNode
                        if (currentNode.getTextContent() == '') {
                            currentNode.replace(firstNode)
                        } else {
                            currentNode.insertAfter(firstNode)
                        }
                        let lastInserted: ParaNode = firstNode
                        restNodes.forEach((node) => {
                            lastInserted.insertAfter(node) as ParaNode
                            lastInserted = node as ParaNode
                        })
                        lastInserted.selectEnd()
                    })
                    setIsDrawerOpen(false)
                }}
            >
                Insert to Story
            </Button>
        )
    }

    function buildReplaceButton() {
        return (
            <Button
                className="flex-1"
                onClick={() => {
                    editor?.update(() => {
                        const currentNode = getCurrentNode() as ParaNode
                        const newNodes = outputNodes.map((text) => {
                            const textContent = text.children
                                .map((child: any) => child.text)
                                .join(' ')
                            const para = ParaNode.create(nanoid())
                            para.append($createTextNode(textContent))
                            return para
                        })
                        const [firstNode, ...restNodes] = newNodes
                        currentNode.replace(firstNode)
                        let lastInserted: ParaNode = firstNode
                        restNodes.forEach((node) => {
                            lastInserted.insertAfter(node) as ParaNode
                            lastInserted = node as ParaNode
                        })
                        lastInserted.selectEnd()
                    })
                    setIsDrawerOpen(false)
                }}
            >
                Replace Story
            </Button>
        )
    }
}
export default StAIWriterChatPlugin
